import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { Link } from "react-router-dom";
import { Alert, Spinner } from "reactstrap";
import { withTranslation } from "react-i18next";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { renderMessageAlert } from "../helpers";
import ReCAPTCHA from "react-google-recaptcha";

const LoginForm = ({ submitCallback, errorData, loading, t }) => {
  const recaptchaRef = useRef(null);

  const [slug, setSlug] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef(null);

  const { error } = errorData;
  const { handleValidSubmit } = submitCallback;

  const submitForm = () => {
    if (formRef.current) {
      setIsLoading(true);
      formRef.current.submit();
      setIsLoading(false);
    }
  };

  const submitAvForm = async (event, values) => {
    setIsLoading(true);
    if(recaptchaRef.current.getValue()) recaptchaRef.current.reset();
    await recaptchaRef.current.executeAsync().then(async (token) =>{
      if(token)     {
        await handleValidSubmit(event, {
          ...values,
          recaptcha_token: token,
        });
      }      
    })
    setIsLoading(false);
  };

  return (
    <div className="p-2">
      <form
        className="d-none"
        action={`${process.env.REACT_APP_API_URL}/auth/sso`}
        method="post"
        ref={formRef}
      >
        <input type="hidden" name="client_slug" value={slug} />
        <input type="hidden" name="redirect_url" value={window.location.href} />
      </form>

      <AvForm
        className="form-horizontal"
        onValidSubmit={(event, values) => {
          submitAvForm(event, values);
        }}
      >
        {error && (
          <Alert color="danger">
            {renderMessageAlert(error?.[0]?.value ?? error, t)}
          </Alert>
        )}
        <div className="form-group">
          <AvField
            name="slug"
            label={t("Slug")}
            className="form-control"
            placeholder={t("Slug")}
            type="text"
            autoComplete="off"
            validate={{
              required: {
                value: true,
                errorMessage: t("Required Field"),
              },
              minLength: {
                value: 3,
                errorMessage: t("Invalid field"),
              },
            }}
            onChange={(e) => setSlug(e?.target?.value)}
          />
        </div>
        <div className="form-group">
          <AvField
            name="email"
            label={t("Email")}
            className="form-control"
            placeholder={t("Email or Username")}
            type="email"
            autoComplete="off"
            validate={{
              required: {
                value: true,
                errorMessage: t("Required Field"),
              },
              email: {
                value: true,
                errorMessage: t("Incorrect email"),
              },
            }}
          />
        </div>
        <div className="form-group">
          <AvField
            name="password"
            label={t("Password")}
            type="password"
            required
            placeholder={t("Password")}
            autoComplete="off"
            validate={{
              required: {
                value: true,
                errorMessage: t("Required Field"),
              },
              minLength: {
                value: 4,
                errorMessage: t("Invalid field"),
              },
              maxLength: {
                value: 30,
                errorMessage: t("Invalid field"),
              },
            }}
          />
        </div>
        <div className="custom-control custom-checkbox">
          <input
            type="checkbox"
            className="custom-control-input"
            id="customControlInline"
          />
          <label className="custom-control-label" htmlFor="customControlInline">
            {t("Remember me")}
          </label>
        </div>
        <div className="mt-3">
          {loading ? (
            <button
              className="btn btn-primary btn-block waves-effect waves-light"
              style={{ cursor: "no-drop" }}
              disabled={true}
            >
              <Spinner
                animation="border"
                style={{
                  height: "19px",
                  width: "19px",
                }}
              />
            </button>
          ) : (
            <>
              <button
                className="btn btn-primary btn-block waves-effect waves-light"
                type="submit"
              >
                {t("Log in")}
              </button>
              <button
                className="btn btn-outline-primary btn-block waves-effect waves-light"
                type="button"
                onClick={submitForm}
                disabled={!slug || isLoading}
              >
                {isLoading ? (
                  <Spinner
                    animation="border"
                    style={{
                      height: "19px",
                      width: "19px",
                    }}
                  />
                ) : (
                  t("Sign in with SSO")
                )}
              </button>
            </>
          )}
        </div>
        <div className="mt-4 text-center">
          <Link to="/forgot-password" className="text-muted">
            <i className="mdi mdi-lock mr-1"></i> {t("Forgot your password?")}
          </Link>
        </div>
      </AvForm>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
        size="invisible"
        badge="bottomright"
      />
    </div>
  );
};

LoginForm.propTypes = {
  submitCallback: PropTypes.shape({
    handleValidSubmit: PropTypes.func.isRequired,
  }),
  errorData: PropTypes.shape({
    error: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      PropTypes.bool,
    ]),
  }),
  loading: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(LoginForm);
